/**
 * B2B Order Simulate
 *
 * Calls the Gadget order simulate API when #b2b-submit-btn is clicked,
 * before proceeding to checkout.
 */

import { getCtx } from '@theme/rs-current-company-location';
import { getCustomerAppBaseUrl } from "@theme/gadget-envs";

/**
 * Parse date string to YYYY-MM-DD
 * @param {string} dateStr - e.g. "02/10/2026", "2/10/2026", "2026-02-10"
 * @returns {string|null}
 */
function toApiDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Parse split deliveries from Shopify's JSON string/array property.
 * Supports verbose `{quantity,_delivery_date}` and compact `{q,d}` entries.
 * @param {unknown} value
 * @returns {Array<{quantity: number, deliveryDate: string}>}
 */
function parseRequestedDeliveries(value) {
  let deliveries = value;
  if (typeof deliveries === 'string') {
    try {
      deliveries = JSON.parse(deliveries);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(deliveries)) return [];

  return deliveries
    .flat(Infinity)
    .map((delivery) => {
      if (!delivery || typeof delivery !== 'object' || Array.isArray(delivery)) return null;
      const quantity = Number(delivery.quantity ?? delivery.q);
      const deliveryDate = toApiDate(delivery._delivery_date ?? delivery.d ?? delivery.date ?? delivery.availableDate);
      if (!Number.isFinite(quantity) || quantity <= 0 || !deliveryDate) return null;
      return { quantity, deliveryDate };
    })
    .filter((delivery) => delivery != null);
}

/**
 * Build address for API (addressLine1, city, companyName, country, state, zipCode)
 */
function buildAddress(source) {
  if (!source) return { addressLine1: '', city: '', companyName: '', country: '', state: '', zipCode: '' };
  const addr = {
    addressLine1: (source.address1 || source.addressLine1 || '').trim(),
    city: (source.city || '').trim(),
    companyName: (source.company || source.companyName || source.label || '').trim(),
    country: (source.country || '').trim(),
    state: (source.state || '').trim(),
    zipCode: (source.zip || source.zipCode || '').trim()
  };
  const line2 = (source.address2 || '').trim();
  if (line2) addr.addressLine2 = line2;
  return addr;
}

/**
 * Resolve a cart line's unit price in cents, mirroring the review page:
 * _price_override property first, then the RTP material list (by SKU or alias).
 * @param {Object} item - Cart line from /cart.js
 * @returns {number|null} unit price in cents, or null if unresolved
 */
function getRtpUnitCents(item) {
  const override = item?.properties?._price_override;
  if (override != null && override !== '') {
    const parsed = Number(override);
    if (Number.isFinite(parsed) && parsed > 0) return Math.round(parsed * 100);
  }
  const sku = (item?.properties?._sku || item?.sku || '').trim();
  const list = window.RTP?.lastCartMaterialList;
  const aliases = window.RTP?.pricingSkuAliases;
  if (!Array.isArray(list) || !sku) return null;
  const match = list.find((m) => {
    const matNum = String(m.materialNumber ?? '').trim();
    if (matNum === sku) return true;
    const raws = aliases?.get(matNum);
    return raws ? raws.some((r) => String(r).trim() === sku) : false;
  });
  if (!match || match.netPrice == null) return null;
  return Math.round(Number(match.netPrice) * 100);
}

/**
 * Compute the order total in dollars from RTP-resolved line prices.
 * B2B variants have $0 Shopify base prices, so cart.total_price is always 0
 * and cannot be used; fall back to it only when nothing resolves via RTP.
 */
function buildTotalOrderAmount(cart) {
  const items = cart?.items || [];
  let cents = 0;
  let resolved = false;
  for (const item of items) {
    const unit = getRtpUnitCents(item);
    if (unit != null) {
      cents += unit * (item.quantity || 0);
      resolved = true;
    }
  }
  if (!resolved) cents = Math.round(cart?.total_price || 0);
  return cents / 100;
}

/**
 * Build order simulate request from cart and checkout data
 * @param {Object} cart - Shopify cart from /cart.js
 * @param {Object} checkoutData - From sessionStorage b2b_checkout_data
 * @param {Object} config - B2B_REVIEW_CONFIG
 * @param {Object} b2bContext - B2BReview.b2bContext (locationId, companyId)
 * @returns {Object}
 */
function buildOrderRequest(cart, checkoutData, config) {
  const ctx = getCtx();
  const attributes = cart?.attributes || {};
  const shippingAddress = checkoutData?.shipping_address || {};
  const billingAddress = checkoutData?.billing_address || checkoutData?.shipping_address || {};
  const shippingMethod = checkoutData?.shipping_method || {};
  const companyGId = document.body?.dataset?.companyId ? 'gid://shopify/Company/' + document.body?.dataset?.companyId : '';

  const storeName = (window.Shopify?.shop || 'dev-customer-resideo').replace('.myshopify.com', '') || 'dev-customer-resideo';

  const companyLocationGid = document.body?.dataset?.locationId ? 'gid://shopify/CompanyLocation/' + document.body?.dataset?.locationId : '';

  let shippingAddr = null;
  // If the shipping address is the current address and the attention is empty, use an empty address. We do not want to send the locations current address to the order simulate API.
  if (shippingAddress.type === 'current' && shippingAddress.attention === '') {
    shippingAddr = buildAddress({
        address1: '',
        address2: '',
        city: '',
        company: '',
        country: '',
        state: '',
        zip: ''
      });
  } else {
    shippingAddr = (shippingAddress.address1 || shippingAddress.address_display)
      ? buildAddress(shippingAddress)
      : buildAddress({
        address1: attributes._shipping_address1,
        address2: attributes._shipping_address2,
        city: attributes._shipping_city,
        company: attributes._shipping_company,
        country: attributes._shipping_country,
        state: attributes._shipping_state,
        zip: attributes._shipping_zip
    });
  }

  const billingSameAsShipping = checkoutData?.billing_same_as_shipping !== false;
  const billingAddr = billingSameAsShipping
    ? shippingAddr
    : (billingAddress.address1
        ? buildAddress(billingAddress)
        : buildAddress({
            address1: attributes._billing_address1,
            address2: attributes._billing_address2,
            city: attributes._billing_city,
            company: attributes._billing_company,
            country: attributes._billing_country,
            state: attributes._billing_state,
            zip: attributes._billing_zip
          }));

  const shipComplete = attributes.ship_complete === 'true' || shippingMethod.ship_complete === true;
  const shippingCondition = attributes._shipping_condition || shippingMethod.shipping_condition_id || '01';
  const carrierExternalId = attributes._carrier_external_id || shippingMethod.carrier_external_id || '0';
  const freightTerm = attributes._freight_term || shippingMethod.freight_term || '001';

  const companyExternalId = ctx?.company_external_id || ctx?.location_external_id || document.body?.dataset?.soldTo || '';

  const lineItems = (cart.items || [])
    .flatMap((item) => {
      const requestedDelivery = item.properties?._requested_delivery || item.properties?._requested_deliveries || item.properties?._delivery_date;
      const lineShipComplete = item.properties?._ship_complete === 'true';
      const sku = item.properties?._sku || '';
      const baseLineItem = {
        itemId: String(item.variant_id || item.id),
        sku: sku || undefined,
        shipCompleteLineLevelOrder: lineShipComplete
      };

      const splitDeliveries = parseRequestedDeliveries(requestedDelivery);
      if (splitDeliveries.length > 0) {
        return splitDeliveries.map((delivery) => ({
          ...baseLineItem,
          quantity: delivery.quantity,
          requestedDeliveryDate: delivery.deliveryDate
        }));
      }

      return [{
        ...baseLineItem,
        quantity: item.quantity,
        requestedDeliveryDate: toApiDate(requestedDelivery)
      }];
    })
    .filter((li) => li.itemId && li.quantity > 0);

  return {
    companyLocationGid: companyLocationGid || undefined,
    companyGid: companyGId || undefined,
    storeName,
    salesArea: document.body?.dataset?.currentSalesArea || undefined,
    header: {
      totalOrderAmount: buildTotalOrderAmount(cart),
      attention: shippingAddress.attention || '',
      purchaseOrderNumber: checkoutData?.payment_method?.po_number || undefined,
      shipToId: document.body?.dataset?.shipTo || undefined,
      carrierExternalId: carrierExternalId || undefined,
      customBillingAddress: billingAddr,
      customShippingAddress: shippingAddr,
      freightTerm: freightTerm || undefined,
      isTestOrder: true,
      customerEmail: document.body.dataset.customerEmail || '',
      shipCompleteOrder: shipComplete,
      shippingCondition: shippingCondition || undefined,
      companyExternalId: companyExternalId || undefined,
      billToAccountExternalId: checkoutData.bill_to_account || '',
      promoCode: attributes._promo_code || '',
      currencyCode: window.rsActiveCurrency()
    },
    lineItems: lineItems
  };
}

/**
 * Call order simulate API
 */
async function callOrderSimulate(apiUrl, body) {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };
  if (window.myJWT) {
    headers.Authorization = `Bearer ${window.myJWT}`;
  }
  return fetch(apiUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });
}

/**
 * Initialize order simulate: intercept submit button, call API, then proceed
 */
/**
 * Show surcharge error banner and disable the submit button when surcharge data is invalid.
 */
function __showSurchargeError() {
  // Show error banner
  const banner = document.getElementById('b2b-surcharge-error-banner');
  if (banner) {
    banner.style.display = 'flex';
  }
  // Disable submit button
  const btn = document.getElementById('b2b-submit-btn');
  if (btn) {
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    btn.textContent = btn.dataset.disabledText || 'Order error';
  }
}

/**
 * Hide both error banners (e.g. after a successful retry).
 */
function __hideErrorBanners() {
  ['b2b-surcharge-error-banner', 'b2b-generic-error-banner'].forEach((id) => {
    const banner = document.getElementById(id);
    if (banner) banner.style.display = 'none';
  });
}

/**
 * Re-enable the submit button and restore its original label (retry affordance).
 */
function __resetSubmitButton() {
  const btn = document.getElementById('b2b-submit-btn');
  if (!btn) return;
  btn.disabled = false;
  btn.removeAttribute('aria-disabled');
  btn.textContent = btn.dataset.originalText || btn.textContent;
}

/**
 * Show Generic Error Banner
 */
function __showGenericError(errors) {
  const banner = document.getElementById('b2b-generic-error-banner');
  if (banner) {
    const bannerParagraph = banner.querySelector('.b2b-review__generic-error-text');
    if (errors && bannerParagraph) {
      bannerParagraph.innerHTML = errors.map((error) => error.message).join('<br>');
    }
    banner.style.display = 'flex';
  }

  // Disable submit button
  const btn = document.getElementById('b2b-submit-btn');
  if (btn) {
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    btn.textContent = btn.dataset.disabledText || 'Order error';
  }
}

/**
 * Check if existing cart surcharge is NaN (stale bad data from a previous session).
 * If so, show the error banner before any API call happens.
 */
function __checkStaleSurcharge() {
  const config = window.B2B_REVIEW_CONFIG || {};
  // Check cart attributes via the server-rendered value
  const serverSurcharge = config.surcharges || '';
  if (serverSurcharge !== '' && serverSurcharge !== '0' && isNaN(Number(serverSurcharge))) {
    __showSurchargeError();
    return true;
  }
  return false;
}

async function init() {
  const btn = document.getElementById('b2b-submit-btn');
  if (!btn) return;

  const config = window.B2B_REVIEW_CONFIG || {};
  const strings = config.strings || {};

  if (!window.B2BReview) return;

  // Check for stale NaN surcharge from cart attributes before proceeding
  if (__checkStaleSurcharge()) {
    return;
  }

  const { cart, b2bContext } = window.B2BReview;
  if (!cart || !cart.items || cart.items.length === 0) {
    alert(strings.cart_empty_alert || 'Your cart is empty.');
    return;
  }

  let checkoutData = null;
  try {
    const data = sessionStorage.getItem('b2b_checkout_data');
    if (data) checkoutData = JSON.parse(data);
  } catch {
    checkoutData = null;
  }

  let orderChecked = false;
  let orderCheckRunning = false;

  const runOrderCheck = async () => {
    // A click can land while the initial check (fired below) is still in flight — without this
    // guard the click would start a second concurrent check instead of waiting for the first.
    if (orderCheckRunning) return;
    orderCheckRunning = true;
    try {
      await __runOrderCheck();
    } finally {
      orderCheckRunning = false;
    }
  };

  const __runOrderCheck = async () => {
    __hideErrorBanners();

    const body = buildOrderRequest(cart, checkoutData, config, b2bContext);
    const apiUrl = `${getCustomerAppBaseUrl()}/api/checkout/order`;

    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    btn.textContent = strings.checking_order || 'Checking order…';

    try {
      const response = await callOrderSimulate(apiUrl, body);
      const responseData = await response.json();

      // A non-2xx from the order-simulate endpoint means the order check itself
      // failed — this is not a surcharge data problem.
      if (!response.ok) {
        const errorMessage = responseData?.sapErrors?.error.message;
        __showGenericError([{ message: errorMessage }]);
        return;
      }

      // If the backend marks the order as failed, show the error banner and block checkout
      if (responseData?.orderResponse?.isSuccess === false) {
        console.error('Order simulate failed:', responseData.orderResponse?.errors);
        __showGenericError(responseData.orderResponse?.errors);
        return;
      }

      const surcharges = (parseFloat(responseData?.orderResponse?.header?.handlingFee) || 0) + (parseFloat(responseData.orderResponse?.header?.otherCharges) || 0) + (parseFloat(responseData.orderResponse?.header?.freightFee) || 0) + (parseFloat(responseData.orderResponse?.header?.expediteFee) || 0);

      // If surcharge calculation produced NaN or a negative value, show error and disable submit
      if (isNaN(surcharges) || surcharges < 0) {
        __showSurchargeError();
        return;
      }

      // Set the _surcharges attribute on the cart
      if (surcharges > 0) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _surcharges: surcharges.toFixed(2) } })
        });
      }

      const surchargesCents = Math.round((surcharges || 0) * 100);
      document.querySelectorAll('.b2b-review__summary-value--surcharges').forEach((el) => {
        el.dataset.surchargesCents = String(surchargesCents);
      });

      window.RTP?.refreshEstimatedCartTotals?.();

      if (window.B2BReview?.fetchCart) {
        await window.B2BReview.fetchCart();
      }

      __resetSubmitButton();
      orderChecked = true;
    } catch (err) {
      // Network error or timeout: show the banner and let the customer retry.
      console.error(err);
      __showGenericError();
      __resetSubmitButton();
    }
  };

  btn.addEventListener('click', async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!orderChecked) {
      await runOrderCheck();
      if (!orderChecked) return;
    }
    await window.B2BReview.submitOrder();
  });

  await runOrderCheck();
}

async function bootstrap() {
  if (window.B2BReviewReady) {
    await window.B2BReviewReady;
  }
  await init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}
